import { useState, useEffect } from 'react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Popover from '@mui/material/Popover'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'

/**
 * Password-protect a published deliverable.
 *
 * SCOPE: protection is ARTIFACT-level (keyed by artifactId in artifact_registry) — one password gates
 * EVERY version (v1..vN and future). This is intentional: "the deliverable is protected", not a single
 * version. Share links are per-version, but the password covers them all.
 *
 * SECURITY: the password is sent ONLY here — a direct browser→worker POST to
 * /api/artifacts/:id/protect — and NEVER through the chat/agent/LLM path. The worker hashes it
 * (PBKDF2) and stores only salt+hash. This component is deliberately self-contained so the password
 * value never enters any chat message, tool input, prompt, or SSE stream.
 */
export function ArtifactProtectControl({ artifactId, initiallyProtected, apiBaseUrl, token }: {
  artifactId: string
  initiallyProtected: boolean
  apiBaseUrl: string
  token: string | null
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [password, setPassword] = useState('')
  const [protectedOn, setProtectedOn] = useState(initiallyProtected)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reflect the catalog's protection state (and clear transient input) when the artifact changes.
  useEffect(() => { setProtectedOn(initiallyProtected) }, [artifactId, initiallyProtected])
  useEffect(() => { setPassword(''); setError(null) }, [artifactId, anchorEl])

  const submit = async (pw: string) => {
    setBusy(true); setError(null)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      const res = await fetch(`${apiBaseUrl}/api/artifacts/${encodeURIComponent(artifactId)}/protect`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ password: pw }), // empty string clears protection
      })
      const data = await res.json().catch(() => ({})) as { ok?: boolean; protected?: boolean; error?: string }
      if (!res.ok || !data.ok) { setError(data.error || 'Could not update protection'); return }
      setProtectedOn(!!data.protected)
      setPassword('')
      setAnchorEl(null)
    } catch {
      setError('Network error — please try again')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Tooltip title={protectedOn ? 'Password protected (all versions) — click to change' : 'Add a password (protects all versions)'}>
        <IconButton
          size="small"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ color: protectedOn ? '#008FA8' : '#aaa' }}
        >
          {protectedOn ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: 280, borderRadius: 2 } } }}
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
          {protectedOn ? 'Change password' : 'Protect this deliverable'}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: '#777', mb: 1.5, lineHeight: 1.4 }}>
          Protects <b>every version</b> of this deliverable — viewers are asked for this password before
          any shared link opens. It never goes through chat.
        </Typography>
        <input
          type="password"
          name="rival-deliverable-password"
          autoComplete="new-password"
          value={password}
          autoFocus
          placeholder="New password"
          disabled={busy}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && password.trim()) submit(password.trim()) }}
          style={{
            width: '100%', padding: '8px 10px', border: '1px solid #d8d3ca', borderRadius: 6,
            fontSize: '0.8rem', marginBottom: 8, boxSizing: 'border-box',
          }}
        />
        {error && <Typography sx={{ fontSize: '0.7rem', color: '#c7442e', mb: 1 }}>{error}</Typography>}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {protectedOn && (
            <button
              type="button"
              disabled={busy}
              onClick={() => submit('')}
              style={{
                fontSize: '0.72rem', padding: '6px 10px', border: '1px solid #d8d3ca',
                borderRadius: 6, background: '#fff', color: '#777', cursor: 'pointer',
              }}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            disabled={busy || !password.trim()}
            onClick={() => submit(password.trim())}
            style={{
              fontSize: '0.72rem', padding: '6px 12px', border: 'none', borderRadius: 6,
              background: password.trim() ? '#008FA8' : '#cbd5d8', color: '#fff',
              cursor: password.trim() ? 'pointer' : 'default', fontWeight: 600,
            }}
          >
            {busy ? 'Saving…' : protectedOn ? 'Update' : 'Protect'}
          </button>
        </Box>
      </Popover>
    </>
  )
}
